<template>
  <div :class="$style.miniNav">
    <template v-for="node in nodes" :key="node.layoutStoreKey || node.label">
      <SCADropdown
        v-if="hasChildren(node)"
        placement="right-start"
        trigger="hover"
        :offset-x="-9"
        :offset-y="8"
        no-padding
        shadow-variant="upward"
        no-mobile-margin
      >
        <template #trigger="{ target }">
          <SCAIconButton
            :as="node.route ? RouterLink : undefined"
            :to="node.route"
            :popovertarget="node.route ? undefined : target"
            size="small"
            :class="node.route ? $style.clickable : $style.notClickable"
            :type="isSelected(node) ? 'toggle' : 'ghost'"
            :selected="isSelected(node)"
            :appearance="isSelected(node) ? 'primary' : 'secondary'"
            :icon="node.icon"
            :title="localizeModuleName(node.label)"
          />
        </template>
        <template #default="{ popover }">
          <SidebarDropdownContainer>
            <template #header>
              <SidebarNavHeader
                v-model="localSearchMap[node.layoutStoreKey || node.label]"
                :is-mini="false"
                :show-settings="false"
                :search-active="!!localSearchMap[node.layoutStoreKey || node.label]"
                :set-all-expanded="undefined"
              />
            </template>
            <template v-if="filteredChildren(node).length > 0">
              <NavTree
                v-for="child in filteredChildren(node)"
                :key="child.layoutStoreKey"
                :nav="child"
                :search-term="localSearchMap[node.layoutStoreKey || node.label]"
                :is-search-active="!!localSearchMap[node.layoutStoreKey || node.label]"
                :module-label="node.label"
                :parent-layout-store-key="node.layoutStoreKey"
                is-mini
                @close="popover?.hidePopover()"
              />
            </template>
            <template v-else>
              <SCANoItems :title="$t('common.emptyState.empty3')" />
            </template>
          </SidebarDropdownContainer>
        </template>
      </SCADropdown>

      <SCAIconButton
        v-else
        :as="node.route ? RouterLink : undefined"
        :to="node.route"
        size="small"
        :class="node.route ? $style.clickable : $style.notClickable"
        :type="isSelected(node) ? 'toggle' : 'ghost'"
        :selected="isSelected(node)"
        :appearance="isSelected(node) ? 'primary' : 'secondary'"
        :icon="node.icon"
        :title="localizeModuleName(node.label)"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { SCADropdown, SCAIconButton, SCANoItems } from "@scayla/components";
import { ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRoute, useRouter } from "vue-router";

import { localizeModuleName } from "@/i18n/helpers";
import type { NavNode } from "@/utils/navUtils";
import { isNodeActive, nodeMatchesSearch } from "@/utils/navUtils";

import NavTree from "../NavTree.vue";
import SidebarDropdownContainer from "./SidebarDropdownContainer.vue";
import SidebarNavHeader from "./SidebarNavHeader.vue";

const { t: $t } = useI18n();

const props = defineProps<{
  nodes: NavNode[];
}>();

const route = useRoute();
const router = useRouter();

const localSearchMap = ref<Record<string, string>>({});

watchEffect(() => {
  props.nodes.forEach((node) => {
    const key = node.layoutStoreKey ?? node.label;
    if (!(key in localSearchMap.value)) {
      localSearchMap.value[key] = "";
    }
  });
});

// for resetting other searches when one becomes non-empty
watch(
  () => Object.entries(localSearchMap.value),
  (newEntries, oldEntries) => {
    const oldMap = Object.fromEntries(oldEntries ?? []);
    const newMap = Object.fromEntries(newEntries ?? []);
    const becameNonEmptyKey = Object.keys(newMap).find(
      (key) => String(oldMap[key] ?? "").trim() === "" && String(newMap[key] ?? "").trim() !== "",
    );
    if (!becameNonEmptyKey) return;
    for (const key of Object.keys(newMap)) {
      if (key !== becameNonEmptyKey && String(localSearchMap.value[key] ?? "") !== "") {
        localSearchMap.value[key] = "";
      }
    }
  },
);

function isSelected(node: NavNode): boolean {
  return isNodeActive(node, route, router);
}

// Leaf nodes (e.g. home) have nothing to show in a dropdown — render only icon
function hasChildren(node: NavNode): boolean {
  return Boolean(node.children?.length);
}

function filteredChildren(node: NavNode) {
  const key = node.layoutStoreKey ?? node.label;
  const search = localSearchMap.value[key]?.toLowerCase() || "";
  if (!search) return node.children ?? [];
  return (node.children ?? []).filter((child) => nodeMatchesSearch(child, search));
}
</script>

<style lang="scss" module>
.miniNav {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-small);
  align-items: center;
}

.miniNav * {
  user-select: none;
}

.miniNav .clickable,
.miniNav .clickable * {
  cursor: pointer;
}

.miniNav .notClickable,
.miniNav .notClickable * {
  cursor: default;
}
</style>
